import React, { useState, useEffect, useMemo } from "react";
import "../../App.css";
import { bookingService, talentService } from "../../api/apiService";

/**
 * Booking statistics — standalone page (NOT in the navigation; open via /admin/stats).
 *
 * Computes live from the bookings list (client-side), scoped by SHOOT YEAR (start_date).
 * Per-user stats use the PROPOSER (proposed_by). Canceled / declined are shown as their
 * own metrics and excluded from "booked"; status 'deleted' is excluded everywhere.
 *
 * "Retried" is intentionally omitted here — it needs the booking_status_history aggregate,
 * which will come from the backend recompute job (see the stats plan).
 */

// --- status grouping -------------------------------------------------------
const ST = {
  booked: ["Booked"],
  confirmed: ["confirmed"],
  canceled: ["canceled", "Canceled"],
  declined: ["declined", "declined request"],
  onHold: ["talentOnHold", "hold"],
  optionReleased: ["optionSolved"],
  open: ["new order", "requested", "optioned"],
};
const EXCLUDED = ["deleted"]; // never counted

// --- helpers ---------------------------------------------------------------
const safeParse = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "object") return v;
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
};
const yearOf = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  return isNaN(dt) ? null : dt.getFullYear();
};
const monthOf = (d) => {
  const dt = new Date(d);
  return isNaN(dt) ? null : dt.getMonth(); // 0-11
};
const fmtEur = (n) =>
  `€ ${(Number(n) || 0).toLocaleString("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function StatsPage() {
  const [bookings, setBookings] = useState([]);
  const [rateMap, setRateMap] = useState({}); // talentKey -> { day, hour }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(""); // "" = whole year, otherwise 0-11

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [bRes, tRes] = await Promise.all([
          bookingService.getAllBookings(1, 30000),
          talentService.getAllTalents(1, 5000),
        ]);
        const allBookings = bRes?.data?.bookings || bRes?.data || [];
        const talents = tRes?.data?.talents || tRes?.data || [];

        // Build a rate lookup keyed by every id form a booking might reference.
        const map = {};
        talents.forEach((t) => {
          const info = {
            day: Number(t.Tagessatz ?? t.rate ?? t.daily_rate) || null,
            hour: Number(t.Stundensatz ?? t.hourlyRate) || null,
            name: (t.Name || t.name || "").trim(),
            email: (t.E_Mail || t.email || "").trim(),
          };
          [t.id, t.firebase_id, t.sql_id, t.Lano_ID].forEach((k) => {
            if (k !== null && k !== undefined && k !== "") map[String(k)] = info;
          });
        });

        setBookings(Array.isArray(allBookings) ? allBookings : []);
        setRateMap(map);
      } catch (err) {
        console.error("Error loading stats:", err);
        setError(err.message || "Failed to load statistics");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Estimate the honorar (fee) for a booking, mirroring the booking detail logic.
  const honorarOf = (b) => {
    const cr = safeParse(b.custom_rate);
    const customVal = cr && cr.value != null ? Number(cr.value) : null;
    const talentRate =
      rateMap[String(b.talent_id)] || rateMap[String(b.sql_talent_id)] || null;
    const billing = b.billing_type || "daily";
    const rate =
      customVal != null
        ? customVal
        : billing === "hourly"
        ? talentRate?.hour
        : talentRate?.day; // KPI has no default talent rate
    if (rate == null || isNaN(rate)) return 0;
    if (billing === "hourly") return rate * (Number(b.booked_hours) || 0);
    if (billing === "kpi") return rate * (Number(b.kpi) || 0);
    return rate;
  };

  // Available years (from shoot date), newest first.
  const years = useMemo(() => {
    const s = new Set();
    bookings.forEach((b) => {
      if (EXCLUDED.includes(b.status)) return;
      const y = yearOf(b.start_date);
      if (y) s.add(y);
    });
    return Array.from(s).sort((a, b) => b - a);
  }, [bookings]);

  // Everything for the selected year (and optional month).
  const data = useMemo(() => {
    const inYear = bookings.filter(
      (b) => !EXCLUDED.includes(b.status) && yearOf(b.start_date) === year
    );
    // The bar chart always reflects the whole year; cards/tables honor the month filter.
    const inScope =
      month === ""
        ? inYear
        : inYear.filter((b) => monthOf(b.start_date) === Number(month));

    const bucket = () => ({
      total: 0,
      booked: 0,
      confirmed: 0,
      canceled: 0,
      declined: 0,
      onHold: 0,
      honorar: 0,
    });
    const classify = (target, b) => {
      target.total += 1;
      if (ST.booked.includes(b.status)) target.booked += 1;
      else if (ST.confirmed.includes(b.status)) target.confirmed += 1;
      else if (ST.canceled.includes(b.status)) target.canceled += 1;
      else if (ST.declined.includes(b.status)) target.declined += 1;
      else if (ST.onHold.includes(b.status)) target.onHold += 1;
      // honorar only counts orders that are actually booked (not merely confirmed)
      if (ST.booked.includes(b.status)) {
        target.honorar += honorarOf(b);
      }
    };

    const totals = bucket();
    const byClient = {};
    const byProposer = {};
    const byLocation = {};
    const byTalent = {};
    const months = Array.from({ length: 12 }, () => 0); // booked+confirmed per month (full year)

    // Monthly series spans the whole year regardless of the month filter.
    inYear.forEach((b) => {
      if (ST.booked.includes(b.status) || ST.confirmed.includes(b.status)) {
        const m = monthOf(b.start_date);
        if (m != null) months[m] += 1;
      }
    });

    inScope.forEach((b) => {
      classify(totals, b);

      const client =
        (b.client && typeof b.client === "object"
          ? b.client.label || b.client.name
          : b.client) ||
        b.client_name ||
        "— none —";
      byClient[client] = byClient[client] || bucket();
      classify(byClient[client], b);

      const proposer = (b.proposed_by && String(b.proposed_by).trim()) || "— none —";
      byProposer[proposer] = byProposer[proposer] || bucket();
      classify(byProposer[proposer], b);

      const loc =
        b.location_short || b.location_name || b.location?.short || b.location?.name || "— none —";
      byLocation[loc] = byLocation[loc] || bucket();
      classify(byLocation[loc], b);

      // Resolve a talent label: name → looked-up name/email → id, else truly none.
      const tInfo = rateMap[String(b.talent_id)] || rateMap[String(b.sql_talent_id)] || null;
      const tId = b.talent_id || b.sql_talent_id;
      const talent =
        (b.talent_name && String(b.talent_name).trim()) ||
        (tInfo && (tInfo.name || tInfo.email)) ||
        (tId ? `ID ${tId}` : "— no talent —");
      byTalent[talent] = byTalent[talent] || bucket();
      classify(byTalent[talent], b);
    });

    const toSortedRows = (obj) =>
      Object.entries(obj)
        .map(([key, v]) => ({ key, ...v }))
        .sort((a, b) => b.total - a.total);

    return {
      count: inScope.length,
      totals,
      months,
      clients: toSortedRows(byClient),
      proposers: toSortedRows(byProposer),
      locations: toSortedRows(byLocation),
      talents: toSortedRows(byTalent),
    };
  }, [bookings, year, month, rateMap]);

  // ---- styling tokens (flat, no border-radius) ----
  const card = {
    borderLeft: "3px solid #121212",
    padding: "12px 18px",
    background: "#f7f7f7",
    minWidth: 0,
  };
  const cardNum = { fontSize: "26px", fontWeight: 800, color: "#121212", lineHeight: 1.1 };
  const cardLbl = {
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".05em",
    color: "#888",
    marginTop: "6px",
  };
  const sectionTitle = {
    fontSize: "12px",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: ".07em",
    color: "#aaa",
    margin: "36px 0 12px",
  };

  const Cards = ({ items }) => (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "16px" }}>
      {items.map((it) => (
        <div style={{ ...card, borderLeftColor: it.color || "#121212" }} key={it.label}>
          <div style={{ ...cardNum, color: it.color || "#121212" }}>{it.value}</div>
          <div style={cardLbl}>{it.label}</div>
        </div>
      ))}
    </div>
  );

  const Table = ({ title, rows, keyHead }) => (
    <>
      <div style={sectionTitle}>{title}</div>
      <div style={{ overflowX: "auto" }}>
        <table className="stats-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "2px solid #121212" }}>
              <th style={{ padding: "8px 10px" }}>{keyHead}</th>
              <th style={{ padding: "8px 10px", textAlign: "right" }}>Total</th>
              <th style={{ padding: "8px 10px", textAlign: "right" }}>Booked</th>
              <th style={{ padding: "8px 10px", textAlign: "right" }}>Confirmed</th>
              <th style={{ padding: "8px 10px", textAlign: "right" }}>Canceled</th>
              <th style={{ padding: "8px 10px", textAlign: "right" }}>Declined</th>
              <th style={{ padding: "8px 10px", textAlign: "right" }}>Honorar</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: "14px 10px", color: "#999" }}>No data</td></tr>
            ) : (
              rows.map((r, i) => (
                <tr key={r.key} style={{ background: i % 2 ? "#f7f7f7" : "#fff" }}>
                  <td style={{ padding: "8px 10px", fontWeight: 600 }}>{r.key}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right" }}>{r.total}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right" }}>{r.booked}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right" }}>{r.confirmed}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", color: r.canceled ? "#c0392b" : "inherit" }}>{r.canceled}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", color: r.declined ? "#c0392b" : "inherit" }}>{r.declined}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600 }}>{fmtEur(r.honorar)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );

  const maxMonth = Math.max(1, ...data.months);
  const scopeLabel = month === "" ? `${year}` : `${MONTHS[Number(month)]} ${year}`;

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "16px" }}>
          <div>
            <p style={{ margin: 0, fontSize: "12px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: "#aaa" }}>
              Statistics
            </p>
            <h1 style={{ margin: "6px 0 0" }}>Booking statistics</h1>
            <p style={{ margin: "8px 0 0", color: "#888", fontSize: "13px" }}>
              By shoot year (start date) · per-user = proposer · canceled/declined shown separately.
            </p>
          </div>
          <div style={{ display: "flex", gap: "12px" }}>
            <div>
              <label style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "#888", display: "block", marginBottom: "4px" }}>
                Year
              </label>
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                style={{ padding: "9px 12px", border: "1px solid #121212", fontSize: "14px", fontWeight: 600, background: "#fff" }}
              >
                {(years.length ? years : [year]).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "#888", display: "block", marginBottom: "4px" }}>
                Month
              </label>
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                style={{ padding: "9px 12px", border: "1px solid #121212", fontSize: "14px", fontWeight: 600, background: "#fff" }}
              >
                <option value="">All months</option>
                {MONTHS.map((m, i) => (
                  <option key={m} value={i}>{m}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <p style={{ marginTop: "40px" }}>Loading statistics… (crunching all bookings)</p>
        ) : error ? (
          <p style={{ marginTop: "40px", color: "#c0392b" }}>{error}</p>
        ) : (
          <>
            <div style={sectionTitle}>Overview — {scopeLabel} ({data.count} orders)</div>
            <Cards
              items={[
                { label: "Total orders", value: data.totals.total },
                { label: "Booked", value: data.totals.booked, color: "#2e7d32" },
                { label: "Confirmed", value: data.totals.confirmed, color: "#15a326" },
                { label: "Canceled", value: data.totals.canceled, color: "#c0392b" },
                { label: "Declined", value: data.totals.declined, color: "#e67e22" },
                { label: "On hold", value: data.totals.onHold, color: "#8e44ad" },
                { label: "Honorar (booked only)", value: fmtEur(data.totals.honorar) },
              ]}
            />

            <div style={sectionTitle}>Booked + confirmed per month — {year}</div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: "10px", height: "180px", borderLeft: "1px solid #cfcfcf", borderBottom: "1px solid #cfcfcf", padding: "0 6px 0 10px" }}>
              {data.months.map((m, i) => {
                const selected = month !== "" && Number(month) === i;
                return (
                  <div
                    key={i}
                    onClick={() => setMonth(String(i))}
                    title={`${MONTHS[i]}: ${m} — click to filter`}
                    style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%", cursor: "pointer", opacity: month === "" || selected ? 1 : 0.45 }}
                  >
                    <span style={{ fontSize: "11px", color: "#666", marginBottom: "4px" }}>{m || ""}</span>
                    <div style={{ width: "100%", background: selected ? "#2e7d32" : "#121212", height: `${(m / maxMonth) * 100}%`, minHeight: m ? "2px" : 0 }} />
                    <span style={{ fontSize: "10px", color: selected ? "#2e7d32" : "#888", fontWeight: selected ? 700 : 400, marginTop: "4px" }}>{MONTHS[i]}</span>
                  </div>
                );
              })}
            </div>

            <Table title="By client" rows={data.clients} keyHead="Client" />
            <Table title="By proposer (user)" rows={data.proposers} keyHead="Proposed by" />
            <Table title="By location" rows={data.locations} keyHead="Location" />
            <Table title="By talent" rows={data.talents} keyHead="Talent" />

            <p style={{ marginTop: "30px", fontSize: "12px", color: "#999", fontStyle: "italic" }}>
              Honorar is estimated from each booking's special rate or the talent's default rate
              (day / hour × hours / KPI × kpi). "Retried" will be added once the backend
              status-history aggregate is in place.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default StatsPage;
